// src/components/Checkout.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import {
  INDIAN_STATES,
  emitCartUpdated,
  getDeliveryLabel,
  getResolvedProductType,
  getShippingPreview,
  isTamilNadu,
} from "../utils/marketplace";
import {
  trackBeginCheckout,
  trackCheckoutFailure,
  trackCouponAttempt,
  trackPurchase,
  trackSupportClick,
} from "../utils/analytics";

const WHATSAPP_NUMBER = "918072968764";



// Load Razorpay script
const loadRazorpay = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("Failed to load Razorpay"));
    document.body.appendChild(s);
  });

const required = (v) => String(v ?? "").trim().length > 0;

const emptyAddress = () => ({
  id: crypto.randomUUID(),
  name: "",
  phone: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  pincode: "",
});

const loadSavedAddresses = () => {
  try {
    return JSON.parse(localStorage.getItem("shipping-addresses") || "[]");
  } catch {
    return [];
  }
};

const saveAddresses = (arr) => {
  localStorage.setItem("shipping-addresses", JSON.stringify(arr));
};

const getMaxQty = (item) => {
  const stock = Number(item?.stockQty);
  return Number.isFinite(stock) && stock > 0 ? stock : Infinity;
};

const resolveCartItemId = (item) =>
  item?._id ||
  item?.id ||
  item?.productId ||
  item?.itemId ||
  item?.product?._id ||
  item?.item?._id ||
  "";

// ✅ Checkout can come from Buy Now OR Cart
const getCheckoutItems = () => {
  try {
    const buyNow = JSON.parse(localStorage.getItem("buy-now-item") || "null");
    if (buyNow && buyNow._id) return { mode: "BUY_NOW", items: [buyNow] };

    const cart = JSON.parse(localStorage.getItem("cart") || "[]");
    if (Array.isArray(cart) && cart.length) return { mode: "CART", items: cart };

    return { mode: "NONE", items: [] };
  } catch {
    return { mode: "NONE", items: [] };
  }
};

const Checkout = () => {
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [checkoutMode, setCheckoutMode] = useState("NONE"); // BUY_NOW | CART
  const [cart, setCart] = useState([]);

  const [deliveryMode, setDeliveryMode] = useState("delivery"); // delivery | pickup

  // Address book
  const [addresses, setAddresses] = useState(loadSavedAddresses());
  const [selectedAddressId, setSelectedAddressId] = useState(
    localStorage.getItem("selected-shipping-address-id") || ""
  );

  const [newAddr, setNewAddr] = useState(emptyAddress());
  const [addingNew, setAddingNew] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState("");
  const [addressConfirmed, setAddressConfirmed] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteSupported, setQuoteSupported] = useState(true);
  const [stockById, setStockById] = useState({});
  const checkoutTrackedRef = useRef(false);
  const couponTrackedRef = useRef("");
  const couponAppliedTrackedRef = useRef("");

  const syncCheckoutItems = (items, mode = checkoutMode) => {
    setCart(items);
    if (mode === "BUY_NOW" && items[0]) {
      localStorage.setItem("buy-now-item", JSON.stringify(items[0]));
      return;
    }
    localStorage.setItem("cart", JSON.stringify(items));
    emitCartUpdated();
  };

  // ✅ WhatsApp helper
  const openWhatsAppChat = (addr) => {
    const message = `Hi, I want delivery to ${addr?.city || ""}, ${addr?.state || ""} (${addr?.pincode || ""}). Can you help?`;
    const text = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, "_blank");
  };

  // ==================================
  // 1) Init customer + checkout items (RUN ONCE)
  // ==================================
  useEffect(() => {
    const loggedCustomer = JSON.parse(localStorage.getItem("customer"));

    if (!loggedCustomer?.token) {
      sessionStorage.setItem("redirectAfterLogin", "checkout");
      navigate("/customer-login");
      return;
    }

    setCustomer(loggedCustomer);

    const checkout = getCheckoutItems();

    if (!checkout.items.length) {
      alert("No items found. Please add product to cart or click Buy Now.");
      navigate("/");
      return;
    }

    setCheckoutMode(checkout.mode);
    setCart(checkout.items);
  }, [navigate]);

  // ==================================
  // 2) Auto select first address (FIX ESLINT WARNING)
  // ==================================
  useEffect(() => {
    if (!selectedAddressId && addresses.length > 0) {
      setSelectedAddressId(addresses[0].id);
      localStorage.setItem("selected-shipping-address-id", addresses[0].id);
    }
  }, [addresses, selectedAddressId]);

  useEffect(() => {
    const loadLatestStock = async () => {
      const productIds = cart
        .map((item) => resolveCartItemId(item))
        .filter(Boolean);

      if (!productIds.length) return;

      try {
        const res = await api.get("/api/items");
        const allItems = Array.isArray(res.data) ? res.data : [];
        const nextStockById = {};
        let hasAdjustment = false;

        const nextCart = cart.map((item) => {
          const productId = resolveCartItemId(item);
          const latest = allItems.find((entry) => String(entry._id) === String(productId));
          const latestStock = Number(latest?.stockQty);

          if (Number.isFinite(latestStock) && latestStock >= 0) {
            nextStockById[productId] = latestStock;
            const safeQty = Math.min(Math.max(1, Number(item.qty || 1)), Math.max(1, latestStock));
            if (safeQty !== Number(item.qty || 1) || item.stockQty !== latestStock) {
              hasAdjustment = true;
              return { ...item, qty: safeQty, stockQty: latestStock };
            }
            return { ...item, stockQty: latestStock };
          }

          return item;
        });

        setStockById(nextStockById);
        if (hasAdjustment) {
          setCart(nextCart);
          if (checkoutMode === "BUY_NOW" && nextCart[0]) {
            localStorage.setItem("buy-now-item", JSON.stringify(nextCart[0]));
          } else {
            localStorage.setItem("cart", JSON.stringify(nextCart));
            emitCartUpdated();
          }
        }
      } catch {
        // Keep local cart snapshot if live stock check fails.
      }
    };

    if (cart.length) {
      loadLatestStock();
    }
  }, [cart, checkoutMode]);

  // ==================================
  // Derived values
  // ==================================
  const selectedAddress = useMemo(() => {
    return addresses.find((a) => a.id === selectedAddressId) || null;
  }, [addresses, selectedAddressId]);

  const subtotal = useMemo(() => {
    return cart.reduce(
      (sum, item) => sum + Number(item.price) * Number(item.qty || 1),
      0
    );
  }, [cart]);

  useEffect(() => {
    const fetchQuote = async () => {
      if (!quoteSupported) return;
      if (!customer?.token || !cart.length) return;
      if (deliveryMode === "delivery" && (!selectedAddress || !addressConfirmed)) {
        setQuote(null);
        return;
      }

      setQuoteLoading(true);
      try {
        const payload = {
          promoCode,
          deliveryMode,
          shipping:
            deliveryMode === "delivery" && selectedAddress
              ? selectedAddress
              : null,
          items: cart
            .map((item) => ({
              productId: String(resolveCartItemId(item)),
              qty: Number(item.qty || 1),
            }))
            .filter((item) => item.productId),
        };

        const response = await api.post("/api/orders/quote", payload, {
          headers: {
            Authorization: `Bearer ${customer.token}`,
            "Content-Type": "application/json",
          },
        });
        setQuote(response.data);
      } catch (err) {
        if (err?.response?.status === 404) {
          setQuoteSupported(false);
        }
        setQuote(null);
      } finally {
        setQuoteLoading(false);
      }
    };

    fetchQuote();
  }, [addressConfirmed, cart, customer, deliveryMode, promoCode, quoteSupported, selectedAddress]);

  // ✅ address validation
  const addressValid = useMemo(() => {
    if (!selectedAddress) return false;
    return (
      required(selectedAddress.name) &&
      required(selectedAddress.phone) &&
      required(selectedAddress.line1) &&
      required(selectedAddress.city) &&
      required(selectedAddress.state) &&
      /^\d{6}$/.test(String(selectedAddress.pincode).trim())
    );
  }, [selectedAddress]);

  const hasLiveFish = useMemo(
    () => cart.some((item) => getResolvedProductType(item) === "live-fish"),
    [cart]
  );

  const canShipHere = useMemo(() => {
    if (!selectedAddress) return false;
    if (!hasLiveFish) return true;
    return isTamilNadu(selectedAddress.state);
  }, [hasLiveFish, selectedAddress]);

  const shippingCost = useMemo(() => {
    if (deliveryMode === "pickup") return 0;
    if (!addressConfirmed) return 0;
    if (!selectedAddress) return 0;

    return cart.reduce((sum, item) => sum + getShippingPreview(item), 0);
  }, [addressConfirmed, cart, deliveryMode, selectedAddress]);

  const totalAmount = quote?.totalAmount ?? subtotal + shippingCost;

  useEffect(() => {
    if (!customer?.token || !cart.length || checkoutTrackedRef.current) return;
    trackBeginCheckout({
      items: cart,
      value: totalAmount,
      deliveryMode,
      promoCode,
      source: checkoutMode === "BUY_NOW" ? "buy_now" : "cart",
    });
    checkoutTrackedRef.current = true;
  }, [cart, checkoutMode, customer, deliveryMode, promoCode, totalAmount]);

  useEffect(() => {
    const code = String(promoCode || "").trim().toUpperCase();
    if (!code || code === couponTrackedRef.current) return undefined;
    const timeout = window.setTimeout(() => {
      trackCouponAttempt({ code, context: "checkout", stage: "entered" });
      couponTrackedRef.current = code;
    }, 700);
    return () => window.clearTimeout(timeout);
  }, [promoCode]);

  useEffect(() => {
    const code = String(promoCode || "").trim().toUpperCase();
    if (!code || !quote) return;
    const appliedKey = `${code}:applied`;
    if (couponAppliedTrackedRef.current === appliedKey) return;
    if (quote.appliedPromotions?.length || quote.discountAmount || quote.freeGiftLabel || quote.freeShippingApplied) {
      trackCouponAttempt({ code, context: "checkout", stage: "applied" });
      couponAppliedTrackedRef.current = appliedKey;
    }
  }, [promoCode, quote]);

  const canPay = useMemo(() => {
    if (!customer?.token) return false;
    if (!cart.length) return false;
    if (loading) return false;
    if (
      cart.some((item) => {
        const productId = resolveCartItemId(item);
        const latestStock = Number(stockById[productId]);
        return Number.isFinite(latestStock) && Number(item.qty || 1) > latestStock;
      })
    ) {
      return false;
    }

    if (deliveryMode === "pickup") return true;

    return !!selectedAddress && addressConfirmed && addressValid && canShipHere;
  }, [customer, cart, loading, deliveryMode, selectedAddress, addressConfirmed, addressValid, canShipHere, stockById]);

  const changeQty = (id, delta) => {
    const next = cart
      .map((item) =>
        resolveCartItemId(item) === id
          ? {
              ...item,
              qty: Math.min(
                Math.max(1, Number(item.qty || 1) + delta),
                Number.isFinite(Number(stockById[id])) && Number(stockById[id]) > 0
                  ? Number(stockById[id])
                  : getMaxQty(item)
              ),
            }
          : item
      )
      .filter(Boolean);
    syncCheckoutItems(next);
  };

  const removeItem = (id) => {
    const next = cart.filter((item) => resolveCartItemId(item) !== id);
    if (!next.length) {
      localStorage.removeItem("cart");
      localStorage.removeItem("buy-now-item");
      emitCartUpdated();
      navigate("/cart");
      return;
    }

    const mode = checkoutMode === "BUY_NOW" && next.length === 1 ? "BUY_NOW" : "CART";
    setCheckoutMode(mode);
    syncCheckoutItems(next, mode);
  };

  // ==================================
  // Address actions
  // ==================================
  const onNewField = (e) => {
    const { name, value } = e.target;
    setNewAddr((a) => ({ ...a, [name]: value }));
  };

  const saveNewAddress = () => {
    setError("");

    const tmp = newAddr;

    const valid =
      required(tmp.name) &&
      required(tmp.phone) &&
      required(tmp.line1) &&
      required(tmp.city) &&
      required(tmp.state) &&
      /^\d{6}$/.test(String(tmp.pincode).trim());

    if (!valid) {
      setError("Please fill full address with valid 6-digit PIN.");
      return;
    }

    const updated = [tmp, ...addresses];
    setAddresses(updated);
    saveAddresses(updated);

    setSelectedAddressId(tmp.id);
    localStorage.setItem("selected-shipping-address-id", tmp.id);

    setAddingNew(false);
    setEditingAddressId("");
    setNewAddr(emptyAddress());
    setAddressConfirmed(false);
  };

  const startEditAddress = () => {
    if (!selectedAddress) {
      setError("Please select an address to edit.");
      return;
    }

    setError("");
    setAddingNew(true);
    setEditingAddressId(selectedAddress.id);
    setNewAddr({ ...selectedAddress });
    setAddressConfirmed(false);
  };

  const updateExistingAddress = () => {
    setError("");

    const tmp = newAddr;
    const valid =
      required(tmp.name) &&
      required(tmp.phone) &&
      required(tmp.line1) &&
      required(tmp.city) &&
      required(tmp.state) &&
      /^\d{6}$/.test(String(tmp.pincode).trim());

    if (!valid) {
      setError("Please fill full address with valid 6-digit PIN.");
      return;
    }

    const updated = addresses.map((addr) =>
      addr.id === editingAddressId ? { ...tmp, id: editingAddressId } : addr
    );

    setAddresses(updated);
    saveAddresses(updated);
    setSelectedAddressId(editingAddressId);
    localStorage.setItem("selected-shipping-address-id", editingAddressId);
    setAddingNew(false);
    setEditingAddressId("");
    setNewAddr(emptyAddress());
    setAddressConfirmed(false);
  };

  const confirmAddress = () => {
    setError("");

    if (!selectedAddress) return setError("Please select an address.");
    if (!addressValid) return setError("Please complete the shipping address correctly.");

    if (!canShipHere) {
      setError("This cart contains live fish, so delivery is available only inside Tamil Nadu.");
      trackSupportClick({ channel: "whatsapp", context: "checkout_shipping_restriction" });
      openWhatsAppChat(selectedAddress);
      return;
    }

    setAddressConfirmed(true);
  };

  // ==================================
  // Payment handler
  // ==================================
  const handlePayment = async () => {
    if (loading) return;
    setLoading(true);
    setError("");

    if (!cart || cart.length === 0) {
      trackCheckoutFailure({
        step: "checkout_validation",
        message: "Cart is empty",
        deliveryMode,
        source: checkoutMode === "BUY_NOW" ? "buy_now" : "cart",
      });
      setError("Cart is empty. Please add products.");
      setLoading(false);
      return;
    }

    try {
      if (deliveryMode === "delivery") {
        if (!selectedAddress) {
          trackCheckoutFailure({
            step: "address_selection",
            message: "Delivery address missing",
            deliveryMode,
            source: checkoutMode === "BUY_NOW" ? "buy_now" : "cart",
          });
          setError("Select delivery address first.");
          setLoading(false);
          return;
        }
        if (!addressConfirmed) {
          trackCheckoutFailure({
            step: "address_confirmation",
            message: "Delivery address not confirmed",
            deliveryMode,
            source: checkoutMode === "BUY_NOW" ? "buy_now" : "cart",
          });
          setError("Please confirm delivery address first.");
          setLoading(false);
          return;
        }
      }

      const normalizedItems = cart.map((item) => ({
        productId: String(resolveCartItemId(item)),
        qty: Number(item.qty || 1),
      }));
      const primaryProductId = normalizedItems[0]?.productId || "";

      if (!primaryProductId || normalizedItems.some((item) => !item.productId)) {
        throw new Error("This cart has an older product reference. Please remove the item and add it again.");
      }

      const stockConflict = cart.find((item) => {
        const productId = resolveCartItemId(item);
        const latestStock = Number(stockById[productId]);
        return Number.isFinite(latestStock) && Number(item.qty || 1) > latestStock;
      });

      if (stockConflict) {
        throw new Error(`Only ${stockById[resolveCartItemId(stockConflict)]} item(s) available for ${stockConflict.name}.`);
      }

      const payload = {
        shipping:
          deliveryMode === "delivery"
            ? {
                name: selectedAddress.name,
                phone: selectedAddress.phone,
                line1: selectedAddress.line1,
                line2: selectedAddress.line2,
                city: (selectedAddress.city || "").trim(),
                state: (selectedAddress.state || "").trim(),
                pincode: selectedAddress.pincode,
              }
            : null,
        deliveryMode,
        itemId: primaryProductId,
        productId: primaryProductId,
        items: normalizedItems,
        promoCode,
      };

      const orderRes = await api.post("/api/orders/create-order", payload, {
        headers: {
          Authorization: `Bearer ${customer.token}`,
          "Content-Type": "application/json",
        },
      });

      const od = orderRes.data;
      if (!od?.id) throw new Error(od?.error || "Failed to create order");
      if (!od.paymentGatewayEnabled) {
        trackPurchase({
          orderId: od.id,
          amount: totalAmount,
          items: cart,
          promoCode,
          deliveryMode,
          paymentMethod: "manual_or_cod",
        });
        alert("Order created. Payment gateway is not configured yet, so the order was marked as received.");
        localStorage.removeItem("cart");
        localStorage.removeItem("buy-now-item");
        emitCartUpdated();
        navigate("/orders");
        return;
      }

      await loadRazorpay();

      const options = {
        key: od.key,
        amount: od.amount, // ✅ paise from backend
        currency: od.currency || "INR",
        name: "Vanigam360",
        description: checkoutMode === "BUY_NOW" ? "Buy Now Checkout" : "Cart Checkout",
        order_id: od.id,

        handler: async function (response) {
          try {
            const verifyPayload = {
              checkoutMode,
              deliveryMode,
              itemId: primaryProductId,
              productId: primaryProductId,
              shipping: deliveryMode === "delivery" ? selectedAddress : null,
              items: normalizedItems,
              promoCode,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            };
            const verifyRes = await api.post("/api/orders/verify-payment", verifyPayload, {
              headers: {
                Authorization: `Bearer ${customer.token}`,
                "Content-Type": "application/json",
              },
            });

            if (verifyRes.data?.success) {
              trackPurchase({
                orderId: response.razorpay_order_id,
                paymentId: response.razorpay_payment_id,
                amount: totalAmount,
                items: cart,
                promoCode,
                deliveryMode,
                paymentMethod: "razorpay",
              });
              alert("Payment successful! Your order has been placed.");

              if (checkoutMode === "BUY_NOW") localStorage.removeItem("buy-now-item");
              else localStorage.removeItem("cart");
              emitCartUpdated();

              navigate("/orders");
            } else {
              trackCheckoutFailure({
                step: "verify_payment",
                message: verifyRes.data?.error || "Payment verification failed",
                deliveryMode,
                source: checkoutMode === "BUY_NOW" ? "buy_now" : "cart",
              });
              setError(verifyRes.data?.error || "Payment verification failed");
            }
          } catch (err) {
            trackCheckoutFailure({
              step: "verify_payment",
              message: err?.response?.data?.error || "Payment verification failed",
              deliveryMode,
              source: checkoutMode === "BUY_NOW" ? "buy_now" : "cart",
            });
            setError(err?.response?.data?.error || "Payment verification failed");
          } finally {
            setLoading(false);
          }
        },

        prefill: {
          name: customer?.name,
          email: customer?.email,
          contact: customer?.phone,
        },
        modal: {
          ondismiss: function () {
            trackCheckoutFailure({
              step: "payment_modal_dismissed",
              message: "Customer closed payment window",
              deliveryMode,
              source: checkoutMode === "BUY_NOW" ? "buy_now" : "cart",
            });
            setLoading(false);
          },
        },
        theme: { color: "#2b6cb0" },
      };

      new window.Razorpay(options).open();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data ||
        err?.message ||
        "Payment failed";
      trackCheckoutFailure({
        step: "create_order",
        message: typeof msg === "string" ? msg : JSON.stringify(msg),
        deliveryMode,
        source: checkoutMode === "BUY_NOW" ? "buy_now" : "cart",
      });
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
      setLoading(false);
    }
  };

  if (!customer) return <p>Loading...</p>;

  return (
    <div style={{ maxWidth: 900, margin: "auto", padding: 20 }}>
      <h2 style={{ marginTop: 0 }}>Checkout</h2>

      <p style={{ marginTop: 0, color: "#6b7280" }}>
        Mode: <b>{checkoutMode === "BUY_NOW" ? "Buy Now" : "Cart"}</b>
      </p>
      <div style={{ marginBottom: 16, padding: 12, borderRadius: 12, background: "#eef6ff", color: "#1e3a8a" }}>
        {hasLiveFish
          ? "This cart contains live fish. Delivery is allowed only within Tamil Nadu."
          : "This cart contains accessories only. Delivery is available across India."}
      </div>
      <div style={{ marginBottom: 16, padding: 12, borderRadius: 12, border: "1px solid #dbeafe", background: "#fff" }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Offers and Coupon</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            value={promoCode}
            onChange={(event) => setPromoCode(event.target.value.toUpperCase())}
            placeholder="Enter coupon code"
            style={{ flex: "1 1 220px", padding: 10, borderRadius: 10, border: "1px solid #cbd5e1" }}
          />
          <button type="button" onClick={() => setPromoCode("")} style={{ padding: "10px 14px" }}>
            Clear Offer
          </button>
        </div>
        {quote?.appliedPromotions?.length ? (
          <div style={{ marginTop: 10, color: "#0f766e", fontSize: 14 }}>
            Applied: {quote.appliedPromotions.map((promo) => promo.title).join(", ")}
          </div>
        ) : null}
        {quote?.appliedPromotions?.some((promo) => promo.firstOrderOnly || promo.type === "first-order") ? (
          <div style={{ marginTop: 8, color: "#0f766e", fontSize: 14, fontWeight: 700 }}>
            New customer first-purchase discount applied automatically.
          </div>
        ) : quote?.firstPurchaseOfferAvailable && quote?.firstPurchaseEligible === false ? (
          <div style={{ marginTop: 8, color: "#64748b", fontSize: 13 }}>
            First-purchase offers are only for customers placing their first paid order.
          </div>
        ) : null}
        {quote?.freeGiftLabel ? (
          <div style={{ marginTop: 6, color: "#7c3aed", fontSize: 14 }}>
            Free gift: {quote.freeGiftLabel}
          </div>
        ) : null}
        {Array.isArray(quote?.freeGiftItems) && quote.freeGiftItems.length ? (
          <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
            {quote.freeGiftItems.map((gift, index) => (
              <div
                key={`${gift.item || gift.name}-${index}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: 10,
                  borderRadius: 12,
                  background: "#f5f3ff",
                  color: "#5b21b6",
                  fontWeight: 700,
                }}
              >
                {gift.imageUrl ? (
                  <img src={gift.imageUrl} alt={gift.name} style={{ width: 42, height: 42, borderRadius: 8, objectFit: "cover" }} />
                ) : null}
                <span>{gift.name} x{gift.qty || 1} will be packed free with this order.</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* Delivery Mode */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
        <button
          type="button"
          onClick={() => {
            setDeliveryMode("delivery");
            setAddressConfirmed(false);
          }}
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            border: "1px solid #ddd",
            background: deliveryMode === "delivery" ? "#111827" : "#fff",
            color: deliveryMode === "delivery" ? "#fff" : "#111827",
            cursor: "pointer",
          }}
        >
          Delivery
        </button>

        <button
          type="button"
          onClick={() => {
            setDeliveryMode("pickup");
            setAddressConfirmed(true);
          }}
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            border: "1px solid #ddd",
            background: deliveryMode === "pickup" ? "#111827" : "#fff",
            color: deliveryMode === "pickup" ? "#fff" : "#111827",
            cursor: "pointer",
          }}
        >
          Store Pickup
        </button>
      </div>

      {/* Address Section (Delivery only) */}
      {deliveryMode === "delivery" && (
        <div style={{ marginBottom: 18, padding: 14, border: "1px solid #eee", borderRadius: 8 }}>
          <h3 style={{ marginTop: 0 }}>Delivery Address</h3>

          {/* Select existing */}
          {addresses.length > 0 && !addingNew && (
            <div style={{ display: "grid", gap: 10 }}>
              <select
                value={selectedAddressId}
                onChange={(e) => {
                  setSelectedAddressId(e.target.value);
                  localStorage.setItem("selected-shipping-address-id", e.target.value);
                  setAddressConfirmed(false);
                }}
              >
                <option value="">Select Address</option>
                {addresses.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} - {a.city}, {a.state} ({a.pincode})
                  </option>
                ))}
              </select>

              {selectedAddress && (
                <div style={{ fontSize: 14, color: "#374151" }}>
                  <div><b>{selectedAddress.name}</b> ({selectedAddress.phone})</div>
                  <div>{selectedAddress.line1}</div>
                  {selectedAddress.line2 && <div>{selectedAddress.line2}</div>}
                  <div>
                    {selectedAddress.city}, {selectedAddress.state} - {selectedAddress.pincode}
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setAddingNew(true);
                  setEditingAddressId("");
                  setAddressConfirmed(false);
                }}
                style={{
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                + Add New Address
              </button>

              {selectedAddress && (
                <button
                  type="button"
                  onClick={startEditAddress}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Edit Selected Address
                </button>
              )}

              <button
                type="button"
                onClick={confirmAddress}
                style={{
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: "none",
                  background: "#111827",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Confirm Delivery Address
              </button>

              {addressConfirmed && (
                <p style={{ margin: 0, color: "#16a34a" }}>
                  ✅ Address confirmed. Shipping updated.
                </p>
              )}
            </div>
          )}

          {/* Add new address */}
          {(addingNew || addresses.length === 0) && (
            <div style={{ display: "grid", gap: 10 }}>
              <input name="name" placeholder="Full Name" value={newAddr.name} onChange={onNewField} />
              <input name="phone" placeholder="Phone" value={newAddr.phone} onChange={onNewField} />
              <input name="line1" placeholder="Address Line 1" value={newAddr.line1} onChange={onNewField} />
              <input name="line2" placeholder="Address Line 2 (optional)" value={newAddr.line2} onChange={onNewField} />
              <input name="city" placeholder="City" value={newAddr.city} onChange={onNewField} />

              <select name="state" value={newAddr.state} onChange={onNewField}>
                <option value="">Select State</option>
                {INDIAN_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>

              <input name="pincode" placeholder="PIN Code (6 digits)" value={newAddr.pincode} onChange={onNewField} />

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  onClick={editingAddressId ? updateExistingAddress : saveNewAddress}
                  style={{
                    flex: 1,
                    padding: "10px 14px",
                    borderRadius: 8,
                    border: "none",
                    background: "#111827",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  {editingAddressId ? "Update Address" : "Save Address"}
                </button>

                {addresses.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setAddingNew(false);
                      setEditingAddressId("");
                      setNewAddr(emptyAddress());
                    }}
                    style={{
                      flex: 1,
                      padding: "10px 14px",
                      borderRadius: 8,
                      border: "1px solid #ddd",
                      background: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Items + Summary */}
      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "1fr 320px" }}>
        <div style={{ padding: 14, border: "1px solid #eee", borderRadius: 8 }}>
          <h3 style={{ marginTop: 0 }}>
            {checkoutMode === "BUY_NOW" ? "Your Item" : "Your Cart"}
          </h3>

          {cart.map((item) => (
            <div
              key={resolveCartItemId(item) || item.name}
              style={{
                display: "flex",
                gap: 10,
                padding: "10px 0",
                borderBottom: "1px solid #f3f4f6",
              }}
            >
              <img
                src={item.imageUrl || item.image}
                alt={item.name}
                style={{ width: 72, height: 72, borderRadius: 10, objectFit: "cover" }}
              />
              <div style={{ flex: 1 }}>
                {Number.isFinite(Number(stockById[resolveCartItemId(item)])) || Number.isFinite(getMaxQty(item)) ? (
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>
                    Available: {Number.isFinite(Number(stockById[resolveCartItemId(item)]))
                      ? Number(stockById[resolveCartItemId(item)])
                      : getMaxQty(item)}
                  </div>
                ) : null}
                <div style={{ fontWeight: 700 }}>{item.name}</div>
                <div style={{ fontSize: 14, color: "#555" }}>
                  ₹{item.price} × {item.qty || 1}
                </div>
                <div style={{ fontSize: 13, color: "#1d4ed8", marginTop: 6 }}>
                  {getDeliveryLabel(item)}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button type="button" onClick={() => changeQty(resolveCartItemId(item), -1)}>-</button>
                  <button
                    type="button"
                    onClick={() => changeQty(resolveCartItemId(item), 1)}
                    disabled={
                      Number(item.qty || 1) >=
                      (Number.isFinite(Number(stockById[resolveCartItemId(item)])) && Number(stockById[resolveCartItemId(item)]) > 0
                        ? Number(stockById[resolveCartItemId(item)])
                        : getMaxQty(item))
                    }
                    style={{
                      opacity:
                        Number(item.qty || 1) >=
                        (Number.isFinite(Number(stockById[resolveCartItemId(item)])) && Number(stockById[resolveCartItemId(item)]) > 0
                          ? Number(stockById[resolveCartItemId(item)])
                          : getMaxQty(item))
                          ? 0.55
                          : 1,
                      cursor:
                        Number(item.qty || 1) >=
                        (Number.isFinite(Number(stockById[resolveCartItemId(item)])) && Number(stockById[resolveCartItemId(item)]) > 0
                          ? Number(stockById[resolveCartItemId(item)])
                          : getMaxQty(item))
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    +
                  </button>
                  <button type="button" onClick={() => removeItem(resolveCartItemId(item))}>Remove</button>
                </div>
              </div>
              <div style={{ fontWeight: 700 }}>
                ₹{(Number(item.price) * Number(item.qty || 1)).toFixed(2)}
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: 14, border: "1px solid #eee", borderRadius: 8 }}>
          <div style={{ fontSize: 20, fontWeight: 700 }}>Order Summary</div>

          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>Subtotal</div>
              <div>₹{subtotal.toFixed(2)}</div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
              <div>Shipping</div>
              <div>
                {deliveryMode === "pickup"
                  ? "Free (Pickup)"
                  : addressConfirmed
                  ? quote?.freeShippingApplied
                    ? Number(quote?.shippingCost || 0) > 0
                      ? `₹${quote.shippingCost} after offer`
                      : "Free"
                    : `₹${quote?.shippingCost ?? shippingCost}`
                  : "Confirm address"}
              </div>
            </div>
            {quote?.shippingDiscount ? (
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, color: "#0f766e" }}>
                <div>Shipping Offer</div>
                <div>-₹{Number(quote.shippingDiscount || 0).toFixed(2)}</div>
              </div>
            ) : null}

            {quote?.discountAmount ? (
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, color: "#0f766e" }}>
                <div>Offer Discount</div>
                <div>-₹{Number(quote.discountAmount || 0).toFixed(2)}</div>
              </div>
            ) : null}
            {Array.isArray(quote?.freeGiftItems) && quote.freeGiftItems.length ? (
              <div style={{ marginTop: 10, padding: 10, borderRadius: 12, background: "#f5f3ff", color: "#5b21b6" }}>
                <strong>Free gift included</strong>
                <div style={{ fontSize: 13, marginTop: 4 }}>
                  {quote.freeGiftItems.map((gift) => `${gift.name} x${gift.qty || 1}`).join(", ")}
                </div>
              </div>
            ) : null}

            <hr style={{ margin: "12px 0" }} />

            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
              <div>Total</div>
              <div>₹{totalAmount.toFixed(2)}</div>
            </div>
            {quoteLoading ? (
              <div style={{ marginTop: 8, fontSize: 13, color: "#64748b" }}>Refreshing offer total...</div>
            ) : null}
          </div>

          {error && (
            <p style={{ color: "red", marginTop: 10 }}>
              {error}
            </p>
          )}

          <button
            onClick={handlePayment}
            disabled={!canPay}
            style={{
              width: "100%",
              marginTop: 12,
              padding: 12,
              backgroundColor: canPay ? "#2b6cb0" : "#93c5fd",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 16,
              cursor: canPay ? "pointer" : "not-allowed",
            }}
          >
            {loading ? "Processing..." : "Pay Now"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
